import React, { useEffect, useId, useRef, useState } from "react";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import CategoryIcon from "@mui/icons-material/Category";
import ComputerIcon from "@mui/icons-material/Computer";
import ConstructionIcon from "@mui/icons-material/Construction";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import PlumbingIcon from "@mui/icons-material/Plumbing";
import SearchIcon from "@mui/icons-material/Search";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

const normalizeSearch = (value = "") => value.toString().trim().toLowerCase();

const getTaxonomySearchResults = (taxonomyAll = {}, searchTerm = "") => {
  const normalizedSearchTerm = normalizeSearch(searchTerm);

  if (!normalizedSearchTerm) {
    return [];
  }

  const results = [];

  Object.entries(taxonomyAll || {}).forEach(([vendorType, typeData]) => {
    (typeData?.disciplines || []).forEach((discipline) => {
      if (normalizeSearch(discipline?.name).includes(normalizedSearchTerm)) {
        results.push({
          id: `${vendorType}-${discipline?._id || discipline?.name}`,
          label: discipline?.name,
          matchType: "Discipline",
          vendorType,
          disciplineName: discipline?.name,
          disciplineId: discipline?._id,
          discipline,
        });
      }

      (discipline?.categories || []).forEach((category) => {
        if (normalizeSearch(category?.name).includes(normalizedSearchTerm)) {
          results.push({
            id: `${vendorType}-${discipline?._id || discipline?.name}-${category?._id || category?.name}`,
            label: category?.name,
            matchType: "Category",
            vendorType,
            disciplineName: discipline?.name,
            disciplineId: discipline?._id,
            categoryName: category?.name,
            categoryId: category?._id,
            category,
          });
        }

        (category?.subCategories || []).forEach((subCategory) => {
          if (normalizeSearch(subCategory?.name).includes(normalizedSearchTerm)) {
            results.push({
              id: `${vendorType}-${discipline?._id || discipline?.name}-${category?._id || category?.name}-${subCategory?._id || subCategory?.name}`,
              label: subCategory?.name,
              matchType: "Sub Category",
              vendorType,
              disciplineName: discipline?.name,
              disciplineId: discipline?._id,
              categoryName: category?.name,
              categoryId: category?._id,
              subCategoryName: subCategory?.name,
              subCategoryId: subCategory?._id,
              category,
              subCategory,
            });
          }
        });
      });
    });
  });

  return results.slice(0, 18);
};

const getTaxonomyResultPath = (result) =>
  [
    result.vendorType,
    result.disciplineName,
    result.categoryName,
    result.subCategoryName,
  ]
    .filter(Boolean)
    .join(" > ");

const getGroupedTaxonomySearchResults = (results = []) =>
  results.reduce((groups, result) => {
    const groupKey = `${result.vendorType}-${result.disciplineName}`;
    const existingGroup = groups.find((group) => group.key === groupKey);

    if (existingGroup) {
      existingGroup.results.push(result);
      return groups;
    }

    return [
      ...groups,
      {
        key: groupKey,
        vendorType: result.vendorType,
        disciplineName: result.disciplineName,
        results: [result],
      },
    ];
  }, []);

const getVendorTypeBadgeClass = (vendorType = "") => {
  const normalizedType = vendorType.toLowerCase();

  if (normalizedType.includes("contractor")) {
    return "bg-[#E6F4D7] text-[#386B10]";
  }

  if (normalizedType.includes("material")) {
    return "bg-[#DCEEFF] text-[#1E5F9F]";
  }

  return "bg-gray-100 text-gray-700";
};

const getTaxonomyMatchBadgeClass = (matchType = "") => {
  if (matchType === "Sub Category") return "bg-[#E7F7F1] text-[#007366]";
  if (matchType === "Category") return "bg-[#FFF1D8] text-[#8A5A00]";
  return "bg-[#ECE9FF] text-[#5B4ABA]";
};

const getDisciplineIcon = (disciplineName = "") => {
  const name = disciplineName.toLowerCase();

  if (name.includes("civil") || name.includes("architect") || name.includes("structure")) {
    return ArchitectureIcon;
  }

  if (name.includes("specialised") || name.includes("specialized")) {
    return WorkspacePremiumIcon;
  }

  if (name.includes("consultant") || name.includes("consultancy")) {
    return PersonIcon;
  }

  if (name.includes("electric") || name.includes("power") || name.includes("solar")) {
    return ElectricalServicesIcon;
  }

  if (name.includes("transport") || name.includes("logistic") || name.includes("truck")) {
    return LocalShippingIcon;
  }

  if (name.includes("it") || name.includes("hardware") || name.includes("software") || name.includes("computer")) {
    return ComputerIcon;
  }

  if (name.includes("plumb") || name.includes("water") || name.includes("pipe")) {
    return PlumbingIcon;
  }

  if (name.includes("hvac") || name.includes("air") || name.includes("ac")) {
    return AcUnitIcon;
  }

  if (name.includes("fire")) {
    return LocalFireDepartmentIcon;
  }

  if (name.includes("contract") || name.includes("construct")) {
    return ConstructionIcon;
  }

  if (name.includes("engineer") || name.includes("mechanical")) {
    return EngineeringIcon;
  }

  return CategoryIcon;
};

const itemId = (item) => String(item?._id || item?.name || "");
const navigationKey = (type, discipline, category) => JSON.stringify([type, itemId(discipline), itemId(category)]);

const VendorTaxonomySearch = ({
  className = "",
  getCategoryRefKey,
  getCategoryError,
  highlightedCategoryRefKey,
  hasSelections,
  hideTaxonomyUntilVendorTypeSelected = false,
  isResultSelected = () => false,
  isVendorTypeSelected = () => false,
  isDisciplineSelected = () => false,
  isCategorySelected = () => false,
  isSubCategorySelected = () => false,
  onCategoryRef,
  onCategoryToggle,
  onClearAll,
  onDisciplineToggle,
  onResultSelect,
  onSearchTermChange,
  onSubCategoryToggle,
  onVendorTypeToggle,
  placeholder = "Search discipline, category, or sub category",
  renderSubCategoryDetails,
  searchTerm = "",
  showTree = false,
  taxonomyAll,
}) => {
  // Labels use the existing selection handlers; arrows only change the visible category.
  const [navigation, setNavigation] = useState({});
  const [mobileItems, setMobileItems] = useState(false);
  const pickerId = useId();
  const browserRef = useRef(null);
  const types = Object.keys(taxonomyAll || {});
  const categoryIndex = [];
  for (const [type, mainTax] of Object.entries(taxonomyAll || {})) {
    for (const discipline of mainTax.disciplines || []) {
      for (const category of discipline.categories || []) categoryIndex.push({ type, discipline, category });
    }
  }
  const selectedGroups = categoryIndex.filter(({ type, discipline, category }) =>
    isVendorTypeSelected(type) && isDisciplineSelected(type, discipline) && isCategorySelected(type, discipline, category)
  ).map((entry) => ({
    ...entry,
    subCategories: (entry.category.subCategories || []).filter((sub) => isSubCategorySelected(entry.type, entry.discipline, entry.category, sub)),
  }));
  const selectedCount = selectedGroups.reduce((total, entry) => total + entry.subCategories.length, 0);
  const anythingSelected = hasSelections ?? (types.some(isVendorTypeSelected) || selectedGroups.length > 0);
  const selectedVendorType = types.find(isVendorTypeSelected);
  const shouldShowTaxonomy =
    !hideTaxonomyUntilVendorTypeSelected || Boolean(selectedVendorType);
  const requested = categoryIndex.find(({ type, discipline, category }) =>
    highlightedCategoryRefKey && getCategoryRefKey?.(type, discipline.name, category.name) === highlightedCategoryRefKey
  ) || categoryIndex.find(({ type, discipline, category }) => getCategoryError?.(type, discipline, category));
  const requestedKey = requested ? navigationKey(requested.type, requested.discipline, requested.category) : "";

  useEffect(() => {
    if (!requestedKey) return;
    const [type, disciplineId, categoryId] = JSON.parse(requestedKey);
    setNavigation({ type, disciplineId, categoryId });
    setMobileItems(true);
  }, [requestedKey]);

  const activeType =
    types.includes(navigation.type) &&
    (!hideTaxonomyUntilVendorTypeSelected || isVendorTypeSelected(navigation.type))
      ? navigation.type
      : selectedVendorType || (hideTaxonomyUntilVendorTypeSelected ? "" : types[0]);
  const disciplines = taxonomyAll?.[activeType]?.disciplines || [];
  const activeDiscipline = disciplines.find((item) => itemId(item) === navigation.disciplineId)
    || disciplines.find((item) => isDisciplineSelected(activeType, item)) || disciplines[0];
  const categories = [...(activeDiscipline?.categories || [])].sort((a, b) => a.name.localeCompare(b.name));
  const activeCategory = categories.find((item) => itemId(item) === navigation.categoryId)
    || categories.find((item) => isCategorySelected(activeType, activeDiscipline, item)) || categories[0];
  const typeSelected = Boolean(activeType && isVendorTypeSelected(activeType));
  const disciplineSelected = Boolean(activeDiscipline && isDisciplineSelected(activeType, activeDiscipline));
  const categorySelected = Boolean(activeCategory && isCategorySelected(activeType, activeDiscipline, activeCategory));
  const canSelectItems = typeSelected && disciplineSelected && categorySelected;
  const activeError = activeCategory && getCategoryError?.(activeType, activeDiscipline, activeCategory);
  const searchResults = getTaxonomySearchResults(taxonomyAll, searchTerm);
  const searchGroups = getGroupedTaxonomySearchResults(searchResults);

  const browse = (type, discipline, category, scroll = false) => {
    setNavigation({ type, disciplineId: itemId(discipline), categoryId: itemId(category) });
    setMobileItems(Boolean(category));
    if (scroll) browserRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const browseResult = (result) => {
    const discipline = taxonomyAll?.[result.vendorType]?.disciplines?.find((item) => itemId(item) === String(result.disciplineId));
    const category = discipline?.categories?.find((item) => itemId(item) === String(result.categoryId));
    browse(result.vendorType, discipline, category);
  };
  const applySearchResult = (result, checked) => {
    browseResult(result);
    onResultSelect?.(result, checked);
    if (showTree && checked) onSearchTermChange("");
  };
  const registerCategory = (entry, node) => onCategoryRef?.({
    node, ...entry, refKey: getCategoryRefKey?.(entry.type, entry.discipline.name, entry.category.name),
  });

  return (
    <div className={"min-w-0 " + className}>
      {shouldShowTaxonomy && <div className="rounded-xl border border-[#E9E6DD] bg-[#FCFCFA] p-3 sm:p-4">
        <label htmlFor={pickerId + "-search"} className="mb-2 block text-xs font-semibold text-gray-700">Find your products and services</label>
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fontSize="small" />
          <input id={pickerId + "-search"} type="text" value={searchTerm} onChange={(event) => onSearchTermChange(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }} placeholder={placeholder}
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-0" />
          {searchTerm && <button type="button" aria-label="Clear category search" onClick={() => onSearchTermChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100"><CloseIcon fontSize="small" /></button>}
        </div>
        <p className="mt-2 text-[11px] text-gray-500">Search directly, or browse below. Makes / brands are optional for Material subcategories.</p>
      </div>}

      {shouldShowTaxonomy && searchTerm && (
        <section aria-label="Category search results" className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          {searchResults.length ? <>
            <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">{searchResults.length} matches. Each result shows its full category path.</p>
            {searchGroups.map((group) => <div key={group.key}>
              <div className="flex items-center gap-2 bg-[#FCFCFA] px-4 py-2 text-xs text-gray-600"><span className={"rounded-full px-2 py-0.5 font-semibold " + getVendorTypeBadgeClass(group.vendorType)}>{group.vendorType}</span>{group.disciplineName}</div>
              {group.results.map((result) => {
                const selected = isResultSelected(result);
                return <div key={result.id} className={"flex items-start gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 " + (selected ? "bg-blue-50/40" : "hover:bg-blue-50/30")}>
                  <input type="checkbox" aria-label={"Select " + getTaxonomyResultPath(result)} checked={selected} onChange={(event) => applySearchResult(result, event.target.checked)} className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300" />
                  <button type="button" title={getTaxonomyResultPath(result)} aria-pressed={selected} onClick={() => applySearchResult(result, !selected)} className="min-w-0 flex-1 text-left">
                    <span className="block text-sm font-semibold text-gray-800">{result.label}</span>
                    <span className="mt-1 block break-words text-xs text-gray-500">{getTaxonomyResultPath(result)}</span>
                  </button>
                  <span className={"shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold " + getTaxonomyMatchBadgeClass(result.matchType)}>{result.matchType}</span>
                </div>;
              })}
            </div>)}
          </> : <p className="px-4 py-3 text-xs text-gray-500">No discipline, category or sub category matches your search.</p>}
        </section>
      )}

      {showTree && (
        <div ref={browserRef} className="mt-4 min-w-0 scroll-mt-24">
          <div role="group" aria-label="Vendor types" className="flex flex-wrap gap-2">
            {types.map((type) => {
              const count = selectedGroups.filter((entry) => entry.type === type).reduce((total, entry) => total + entry.subCategories.length, 0);
              return <div key={type} className={"inline-flex items-center gap-2 rounded-lg border px-3 py-2 " + (activeType === type ? "border-blue-300 bg-blue-50/60" : "border-gray-200 bg-white")}>
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-800">
                  <input type="checkbox" aria-label={"Select " + type + " vendor type"} checked={isVendorTypeSelected(type)}
                    onChange={(event) => { browse(type); onVendorTypeToggle?.(type, event.target.checked); }} className="h-4 w-4 rounded border-gray-300" />
                  <span>{type}</span><span className="rounded-full bg-white px-1.5 text-[10px] text-gray-500">{count}</span>
                </label>
                <button type="button" aria-pressed={activeType === type} aria-label={"Browse " + type} title={"Browse " + type + " without changing selections"} onClick={() => browse(type)} className="rounded p-0.5 text-gray-400 hover:bg-blue-100 hover:text-blue-700"><ChevronRightIcon sx={{ fontSize: 16 }} /></button>
              </div>;
            })}
          </div>
          {shouldShowTaxonomy && <>
          <div className="mt-3 min-w-0 rounded-xl border border-[#E7E5DE] bg-white">
            <div className="border-b border-[#ECEAE4] bg-[#FCFCFA] px-3 py-3 sm:px-4">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-1"><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Disciplines</p><span className="text-[10px] text-gray-400">Click names to select. Use arrows to browse.</span></div>
              <div className="flex max-h-32 flex-wrap gap-2 overflow-y-auto">
                {disciplines.map((discipline) => {
                  const selected = isDisciplineSelected(activeType, discipline);
                  const current = itemId(discipline) === itemId(activeDiscipline);
                  const Icon = getDisciplineIcon(discipline.name);
                  return <div key={itemId(discipline)} className={"inline-flex min-w-0 items-center gap-1.5 rounded-md border px-2 py-1.5 " + (current ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white")}>
                    <label className={"inline-flex min-w-0 items-center gap-1.5 text-xs text-gray-700 " + (typeSelected ? "cursor-pointer" : "cursor-not-allowed opacity-50")}>
                      <input type="checkbox" aria-label={"Select " + discipline.name + " discipline"} checked={selected} disabled={!typeSelected}
                        onChange={(event) => { browse(activeType, discipline); onDisciplineToggle?.(activeType, discipline, event.target.checked); }} className="h-3.5 w-3.5 shrink-0 rounded border-gray-300" />
                      <Icon sx={{ fontSize: 15 }} /><span className="break-words">{discipline.name}</span>
                    </label>
                    <button type="button" aria-pressed={current} aria-label={"Browse " + discipline.name + " discipline"} title={"Browse " + discipline.name + " without changing selections"} onClick={() => browse(activeType, discipline)} className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-blue-100 hover:text-blue-700"><ChevronRightIcon sx={{ fontSize: 14 }} /></button>
                  </div>;
                })}
                {!disciplines.length && <p className="text-xs text-gray-500">No disciplines available for this vendor type.</p>}
              </div>
            </div>
            <div className="grid min-w-0 md:grid-cols-3">
              <nav aria-label="Category browser" className={(mobileItems ? "hidden md:block " : "") + "min-w-0 border-b border-gray-200 md:border-b-0 md:border-r"}>
                <div className="flex items-center justify-between px-4 py-3"><h4 className="text-xs font-semibold text-gray-800">Categories</h4><span className="text-[11px] text-gray-400">{categories.length}</span></div>
                <div className="max-h-80 overflow-y-auto px-2 pb-2">
                  {categories.map((category) => {
                    const entry = { type: activeType, discipline: activeDiscipline, category };
                    const selected = isCategorySelected(activeType, activeDiscipline, category);
                    const current = itemId(category) === itemId(activeCategory);
                    const error = getCategoryError?.(activeType, activeDiscipline, category);
                    const highlighted = Boolean(highlightedCategoryRefKey && getCategoryRefKey?.(activeType, activeDiscipline.name, category.name) === highlightedCategoryRefKey);
                    const count = (category.subCategories || []).filter((sub) => isSubCategorySelected(activeType, activeDiscipline, category, sub)).length;
                    return <div key={itemId(category)} ref={(node) => registerCategory(entry, node)} className={"mb-1 flex items-start gap-2 rounded-lg border p-2 " + (error ? "border-red-200 bg-red-50" : highlighted || current ? "border-blue-200 bg-blue-50/60" : "border-transparent hover:bg-gray-50")}>
                      <label className={"flex min-w-0 flex-1 items-start gap-2 " + (typeSelected && disciplineSelected ? "cursor-pointer" : "cursor-not-allowed opacity-50")}>
                        <input type="checkbox" aria-label={"Select " + category.name + " category"} checked={selected} disabled={!typeSelected || !disciplineSelected}
                          onChange={(event) => { browse(activeType, activeDiscipline, category); onCategoryToggle?.(activeType, activeDiscipline, category, event.target.checked); }} className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300" />
                        <span className="min-w-0"><span className="block break-words text-xs font-medium text-gray-800">{category.name}</span><span className="mt-1 block text-[10px] text-gray-400">{selected && count ? count + " selected" : (category.subCategories?.length || 0) + " subcategories"}</span></span>
                      </label>
                      <button type="button" aria-current={current ? "true" : undefined} aria-label={"Browse " + category.name + " category"} title={"Browse " + category.name + " without changing selections"} aria-controls={pickerId + "-items"} onClick={() => browse(activeType, activeDiscipline, category)} className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-blue-100 hover:text-blue-700">
                        <ChevronRightIcon sx={{ fontSize: 16 }} className="shrink-0 text-gray-400" />
                      </button>
                    </div>;
                  })}
                  {!categories.length && <p className="px-2 py-5 text-xs text-gray-500">No categories available in this discipline.</p>}
                </div>
              </nav>
              <section id={pickerId + "-items"} ref={(node) => { if (activeCategory) registerCategory({ type: activeType, discipline: activeDiscipline, category: activeCategory }, node); }} aria-label="Subcategory browser" className={(mobileItems ? "" : "hidden md:block ") + "min-w-0 md:col-span-2"}>
                <div>
                  <div className="border-b border-[#ECEAE4] px-4 py-3 sm:px-5">
                    <button type="button" onClick={() => setMobileItems(false)} className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-gray-500 md:hidden"><ArrowBackIcon sx={{ fontSize: 15 }} /> Categories</button>
                    <p className="break-words text-[10px] text-gray-400">{activeType}{activeDiscipline ? " / " + activeDiscipline.name : ""}</p>
                    <h4 className="mt-1 text-sm font-semibold text-gray-900">{activeCategory?.name || "Choose a category"}</h4>
                    <p className="mt-1 text-[11px] text-gray-500">Select the subcategories you supply. Add makes only where relevant.</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto p-3 sm:p-4">
                    {!typeSelected || !disciplineSelected ? <p className="rounded-lg border border-dashed border-gray-200 bg-[#FCFCFA] p-4 text-xs leading-5 text-gray-500">{!typeSelected ? "Select the " + (activeType || "") + " vendor type above to choose categories." : "Select the " + activeDiscipline?.name + " discipline above to choose categories."}</p>
                      : activeCategory && !categorySelected ? <div className="rounded-lg border border-dashed border-blue-200 bg-blue-50/30 p-4"><p className="text-xs text-gray-600">Include this category to select its subcategories.</p><button type="button" onClick={() => onCategoryToggle?.(activeType, activeDiscipline, activeCategory, true)} className="mt-3 rounded-md border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700">Select category</button></div>
                      : activeCategory?.subCategories?.length ? <div className="divide-y divide-[#ECEAE4]">
                        {[...activeCategory.subCategories].sort((a, b) => a.name.localeCompare(b.name)).map((subCategory) => {
                          const selected = canSelectItems && isSubCategorySelected(activeType, activeDiscipline, activeCategory, subCategory);
                          const details = selected && renderSubCategoryDetails?.(activeType, activeDiscipline, activeCategory, subCategory);
                          return <div key={itemId(subCategory)} className={"flex min-w-0 flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between " + (selected ? "text-gray-900" : "text-gray-600")}>
                            <label className="inline-flex min-w-0 cursor-pointer items-start gap-2 text-xs sm:flex-1">
                              <input type="checkbox" aria-label={subCategory.name} checked={selected} disabled={!canSelectItems} onChange={(event) => onSubCategoryToggle?.(activeType, activeDiscipline, activeCategory, subCategory, event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300" />
                              <span className="break-words font-medium">{subCategory.name}</span>
                            </label>
                            {details && <div className="w-full min-w-0 pl-6 sm:w-[60%] sm:pl-0">{details}</div>}
                          </div>;
                        })}
                      </div> : <p className="py-3 text-xs text-gray-500">No subcategories available for this category.</p>}
                    {activeError && <p role="alert" className="mt-3 text-xs font-medium text-red-600">{activeError}</p>}
                  </div>
                </div>
              </section>
            </div>
          </div>

          <section aria-label="Selected categories" className="mt-4 rounded-xl border border-[#E7E5DE] bg-[#FCFCFA]">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#ECEAE4] px-4 py-3">
              <div><h4 className="text-xs font-semibold text-gray-800">Your selections</h4><p className="mt-0.5 text-[11px] text-gray-500">{selectedGroups.length} categories / {selectedCount} subcategories selected</p></div>
              {anythingSelected && onClearAll && <button type="button" onClick={onClearAll} className="text-xs font-semibold text-gray-500 underline hover:text-red-600">Clear All</button>}
            </div>
            <div className="max-h-64 divide-y divide-[#ECEAE4] overflow-y-auto">
              {selectedGroups.map((entry) => <div key={navigationKey(entry.type, entry.discipline, entry.category)} ref={(node) => { if (navigationKey(entry.type, entry.discipline, entry.category) !== navigationKey(activeType, activeDiscipline, activeCategory)) registerCategory(entry, node); }} className="px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <button type="button" title={[entry.type, entry.discipline.name, entry.category.name].join(" > ")} onClick={() => browse(entry.type, entry.discipline, entry.category, true)} className="min-w-0 break-words text-left text-[11px] font-semibold text-gray-700 hover:text-blue-700">{entry.type} / {entry.discipline.name} / {entry.category.name}</button>
                  <button type="button" aria-label={"Remove " + entry.category.name + " category"} onClick={() => onCategoryToggle?.(entry.type, entry.discipline, entry.category, false)} className="shrink-0 rounded-full p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><CloseIcon sx={{ fontSize: 15 }} /></button>
                </div>
                <div className="mt-2 space-y-2">
                  {entry.subCategories.map((sub) => <div key={itemId(sub)} className="flex min-w-0 flex-wrap items-center justify-start gap-x-2 gap-y-1.5">
                    <div className="flex min-w-0 items-center gap-1.5"><span className="break-words text-xs text-gray-700">{sub.name}</span><button type="button" aria-label={"Remove " + sub.name + " subcategory"} onClick={() => onSubCategoryToggle?.(entry.type, entry.discipline, entry.category, sub, false)} className="shrink-0 rounded-full p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><CloseIcon sx={{ fontSize: 13 }} /></button></div>
                    {renderSubCategoryDetails?.(entry.type, entry.discipline, entry.category, sub, { summary: true })}
                  </div>)}
                  {!entry.subCategories.length && <p className={"text-[11px] " + (getCategoryError?.(entry.type, entry.discipline, entry.category) ? "text-red-600" : "text-gray-400")}>{getCategoryError?.(entry.type, entry.discipline, entry.category) || "No subcategories selected."}</p>}
                </div>
              </div>)}
              {!selectedGroups.length && <p className="px-4 py-4 text-xs text-gray-400">Selected categories and their makes will appear here.</p>}
            </div>
          </section>
          </>}
        </div>
      )}
    </div>
  );
};

export default VendorTaxonomySearch;
